import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProfileDetailComponent } from 'src/app/pages/people/person/profile-detail.component';
import { AuthenticationService } from '../../core/services/authentication.service';
import { LogCategory, LogType, LogWidget, LoggerService } from '../../core/services/logger.service';
import { ProfileService } from '../../core/services/profile.service';
import { UtilityService } from '../../core/services/utility.service';
import { EnrollmentsService } from '../../services/enrollments.service';
import { IWidgetComponent } from '../../shared/widget/i-widget-component';
import { Widget } from '../../shared/widget/widget';
import { MyCoursesConflictsModalComponent } from './conflicts-modal/my-courses-conflicts.component';
import { MyCoursesRecordingRequestModalComponent } from './recording-request-modal/my-courses-recording-request.component';


@Component({
    selector: 'app-my-courses',
    templateUrl: './my-courses.component.html',
    styleUrls: ['./my-courses.component.scss'],
    standalone: false
})
export class MyCoursesComponent implements OnInit, OnDestroy, IWidgetComponent
{
    public data: Widget;
    public enrollments$: Observable<any>;
    public lmsLink: string;

    public percent: number; // percent complete 
    public options: any;    // eacy piechart options
    public todo: any;
    public todos: any[];
    public registration: boolean;
    public rounds: any[];
    public showWaitlisted: boolean = true;
    private _registrationSubscriber: Subscription;

    private _enrollmentSubscription: Subscription;
    
    @HostBinding('class')
    get size(): string {
        var widgetSize = 'col-md-' + this.data.Size.toString();

        switch (this.data.Size) {
            case 4: {
                widgetSize += ' widget-small';
                break;
            }
            case 6: {
                widgetSize += ' widget-medium';
                break;
            }
            case 12: {
                widgetSize += ' widget-large';
                break;
            }
        }

        return widgetSize;
    }

    public getTitle(pageNumber: number, currentYear: number) {

        switch (pageNumber) {
            case 0:
                return "Current Classes " + "(" + (currentYear) + "/" + (currentYear + 1) + ")";
            case 1:
                return "Upcoming Classes";
            default:
                return (currentYear + pageNumber + 1) + "/" +  (currentYear + pageNumber + 2 ) + " Classes";

        }

    }

    public getTermName(terms, termId) {

        let result = "";
        let term = this.getTerm(terms, termId);
        if (term != null && term.length == 1) {
            result = term[0]?.Program + " " + term[0]?.FriendlyName;
        }
        return result;
    }

    public getTerm(terms, termId) {
        return terms.filter(obj => { return obj.Ids.indexOf(termId.TermId) > -1 });
    }

    public open(content, sectionTodos) {
        this.todos = [sectionTodos.length];
        for (let i = 0; i < sectionTodos.length; i++) {
            this.todos[i] = sectionTodos[i];
        };

        this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
    }
        
     public get todoList(): any[] {
        return this.todos;
    }

    public openProfile(openedInstructor) {
        const modalRef = this.modalService.open(ProfileDetailComponent);
        modalRef.componentInstance.person = openedInstructor;
        modalRef.componentInstance.searchterm = "";
    }

    public openRecordingRequest(section, studentId) {
        const modalRef = this.modalService.open(MyCoursesRecordingRequestModalComponent, { size: 'lg' });
        modalRef.componentInstance.section = section;
        modalRef.componentInstance.studentId = studentId;
    }
    
    constructor(
        public auth: AuthenticationService,
        public enrollmentService: EnrollmentsService,
        public profile: ProfileService,
        private logger: LoggerService,        
        private modalService: NgbModal,
        private utilityService: UtilityService
    ) {
        this.log("constructor", null, LogCategory.Constructor);

        switch (this.auth.token.payload.PrimaryProgram) {
            case 'MbaO':
                this.lmsLink = 'https://digitalcampus.instructure.com';
                break;
            default:
                this.lmsLink = 'https://kenanflagler.instructure.com';

        }

        this.percent = 0;
        this.options = {
            barColor: '#4B9CD3',
            trackColor: '#f9f9f9',
            scaleColor: '#dfe0e0',
            scaleLength: 0,
            lineCap: 'round',
            lineWidth: 8,
            size: 50,
            rotate: 0,
            animate: {
                duration: 1000,
                enabled: true
            }
        };

        this._registrationSubscriber = enrollmentService.hasActiveDropAdd$.subscribe(
            (rounds) => {
                if (rounds.some(i => i.active == true)) {
                    this.registration = true;
                } else {
                    this.registration = false;
                }
                this.rounds = rounds;               
            }
        );             
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.MyCourses, category);
    }

    ngOnInit(): void {
        this.log("ngOnInit", null, LogCategory.Init);

        const computeShowRecordingButton = (enrollment: any): boolean => {
            if (!enrollment?.Section?.EndDate) return false;
            
            const endDate = new Date(enrollment.Section.EndDate);
            const now = this.utilityService.getCurrentDate();
            
            const program = (enrollment.Section.Program || '').toUpperCase();

            if (
                endDate >= now && 
                !enrollment.Section.IsRegistrationCourse && 
                !["MBAO", "MACO", "PHD"].includes(program)
            ) {
                return true;
            }
            return false;
        };

        this.enrollments$ = this.enrollmentService.enrollments$.pipe(
            map((data) => {
                if (this.data.meta?.Data?.hasOwnProperty('SortSectionsByTitle')
                    && this.data.meta.Data['SortSectionsByTitle']
                    && (this.data.meta.Data['SortSectionsByTitle'] == 'false')) {
                    this.enrollmentService.sortSectionsByTitle = false;
                }

                if (data) {
                    if (this.enrollmentService.sortSectionsByTitle) {
                        data.enrollments.sort((a, b) => {
                            return a.Section.StartDate < b.Section.StartDate ? -1 : 1;
                        });
                    }

                    data.enrollments.forEach((enrollment: any) => {
                        enrollment.ShowRecordingButton = computeShowRecordingButton(enrollment);
                    });
                }

                return data;
            })
        )

        //  If the student has set a preference on showing/hiding waitlisted sections apply it
        var componentName = this.data.meta.Name;

        if (this.profile.profile.components.prefs[componentName]?.showWaitlisted)
            this.showWaitlisted = JSON.parse(this.profile.profile.components.prefs["MyCourses"].showWaitlisted);
    }

    ngOnDestroy() {
        this.log("ngOnDestroy", "", LogCategory.Destroy);
        if (this._enrollmentSubscription)
            this._enrollmentSubscription.unsubscribe();
        if (this._registrationSubscriber)
            this._registrationSubscriber.unsubscribe();
    }

    public hasActiveRound(rounds, roundIds) {
        return roundIds.some(id => rounds[id] && rounds[id].dropping);        
    }

    private drop(section, buttonType) {
        this.enrollmentService.confirmDrop(section, buttonType);
    }

    public manage(enrollment: any, allData: any) {
        var conflicts = allData.enrollments.filter(e => e.Conflicts.length);

        var modalRef = this.modalService.open(MyCoursesConflictsModalComponent, {size: 'lg'});
        modalRef.componentInstance.enrollment = enrollment;
        modalRef.componentInstance.instructors = allData.instructors;
        modalRef.componentInstance.moreConflicts = conflicts.length > 2;

    }

    public isDroppable(sectionId, enrollments) {
        //get the list of conflicts where the sectionId matches        
        var conflicts = enrollments.filter(e => e.Conflicts.length > 0).flatMap(c => c.Conflicts);
        var childConflicts = conflicts.flatMap(c => c.Conflicts).filter( s => s.SectionId == sectionId && s.IsDroppable);        
        return childConflicts.length > 0;        
    }

    public changeSectionSorting(sorting:boolean) {

        if (this.enrollmentService.sortSectionsByTitle != sorting) {
            this.enrollmentService.sortSectionsByTitle = sorting;
            this.data.meta.Data['SortSectionsByTitle'] = sorting;

            // Update the widget metadata
            this.profile.updateWidgetPreference(this.data.meta);

            this.enrollments$ = this.enrollments$.pipe(map((data) => {
                data.enrollments.sort((a, b) => {
                    if (sorting)
                        return a.Section.Title < b.Section.Title ? -1 : 1;
                    else
                        return a.Section.StartDate < b.Section.StartDate ? -1 : 1;
                });
                return data;
            }));
        }

    }

    public updateWaitlistDisplay() {
        this.showWaitlisted = !this.showWaitlisted;

        this.profile.updateComponentPreference("MyCourses", {showWaitlisted: this.showWaitlisted});
    }
}
